import {MoneyType, LoginType, AppParams, WalletType} from "app/types/common";
import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {RootState} from "app/store";
import DeviceDetector from "device-detector-js";

export interface SettingState {
    source: string;
    countryCode: string;
    inviteCode: string;
    refreshServeTime: number;
    isDeposit: boolean;
    maxChargeRewardRate: number;
    serverTime: number;
    promoCode: string;
    showDownload: boolean;
    walletCurrency: number;
    walletChannel: string;
    maintainTime: number;
    sideFold: boolean;
    isMobile: boolean;
    loginType: LoginType;
    affilkaTag: string;
    walletType: WalletType;
    isFiat: boolean;
    hideZeroBalance: boolean;
    weekRewardRate: number;
    monthRewardRate: number;
    weekRewardApprovedRate: number;
    monthRewardApprovedRate: number;
    searchGameModalOpen: boolean;
    isGTMInitialized?: boolean;
    device: DeviceDetector.DeviceDetectorResult | null;
}

const initialState: SettingState = {
    source: "",
    countryCode: "",
    inviteCode: "",
    promoCode: "",
    refreshServeTime: 0,
    serverTime: 0,
    maxChargeRewardRate: 0,
    isDeposit: true,
    showDownload: true,
    walletCurrency: MoneyType.BRL,
    walletChannel: "",
    maintainTime: 0,
    sideFold: false,
    isMobile: false,
    loginType: LoginType.HIDE,
    affilkaTag: "",
    walletType: WalletType.Hide,
    isFiat: false,
    hideZeroBalance: false,
    weekRewardRate: 0,
    monthRewardRate: 0,
    weekRewardApprovedRate: 0,
    monthRewardApprovedRate: 0,
    searchGameModalOpen: false,
    isGTMInitialized: false,
    device: null,
};

export const counterSlice = createSlice({
    name: "setting",
    initialState,
    reducers: {
        setCountry: (state, action: PayloadAction<string>) => {
            state.countryCode = action.payload;
        },
        setSource: (state, action: PayloadAction<string>) => {
            state.source = action.payload;
        },
        setInviteCode: (state, action: PayloadAction<string>) => {
            state.inviteCode = action.payload;
        },
        setPromoCode: (state, action: PayloadAction<string>) => {
            state.promoCode = action.payload;
        },
        setWalletCurrency: (state, action: PayloadAction<number>) => {
            state.walletCurrency = action.payload;
        },
        setWalletChannel: (state, action: PayloadAction<string>) => {
            state.walletChannel = action.payload;
        },
        setShowDownload: (state, action: PayloadAction<boolean>) => {
            state.showDownload = action.payload;
        },
        setMaxChargeRate: (state, action: PayloadAction<number>) => {
            state.maxChargeRewardRate = action.payload;
        },
        setServerTime: (state, action: PayloadAction<number>) => {
            state.serverTime = action.payload;
        },
        setRefreshServerTime: (state) => {
            state.refreshServeTime = state.refreshServeTime + 1;
        },
        setIsDeposit: (state, action: PayloadAction<boolean>) => {
            state.isDeposit = action.payload;
        },
        setMaintainTime: (state, action: PayloadAction<number>) => {
            state.maintainTime = action.payload;
        },
        setIsMobile: (state, action: PayloadAction<boolean>) => {
            state.isMobile = action.payload;
        },
        toggleSideFold: (state) => {
            state.sideFold = !state.sideFold;
        },
        setSideFold: (state, action: PayloadAction<boolean>) => {
            state.sideFold = action.payload;
        },
        setLoginType: (state, action: PayloadAction<LoginType>) => {
            state.loginType = action.payload;
        },
        setAffilkaTag: (state, action: PayloadAction<string>) => {
            state.affilkaTag = action.payload;
        },
        setWalletType: (state, action: PayloadAction<WalletType>) => {
            state.walletType = action.payload;
        },
        setIsFiat: (state, action: PayloadAction<boolean>) => {
            state.isFiat = action.payload;
        },
        setHideZeroBalance: (state, action: PayloadAction<boolean>) => {
            state.hideZeroBalance = action.payload;
        },
        setWeekRewardRate: (state, action: PayloadAction<number>) => {
            state.weekRewardRate = action.payload;
        },
        setMonthRewardRate: (state, action: PayloadAction<number>) => {
            state.monthRewardRate = action.payload;
        },
        setWeekRewardApprovedRate: (state, action: PayloadAction<number>) => {
            state.weekRewardApprovedRate = action.payload;
        },
        setMonthRewardApprovedRate: (state, action: PayloadAction<number>) => {
            state.monthRewardApprovedRate = action.payload;
        },
        setSearchGameModalOpen: (state, action: PayloadAction<boolean>) => {
            state.searchGameModalOpen = action.payload;
        },
        setGTMInitialized(state, action: PayloadAction<boolean>) {
            state.isGTMInitialized = action.payload;
        },
        setDevice(state, action: PayloadAction<DeviceDetector.DeviceDetectorResult>) {
            state.device = action.payload;
        },
    },
});

export const isSideFold = (state: RootState) => state.setting.sideFold;

export const {
    setCountry,
    setSource,
    setInviteCode,
    setPromoCode,
    setWalletCurrency,
    setWalletChannel,
    setMaxChargeRate,
    setServerTime,
    setRefreshServerTime,
    setIsDeposit,
    setShowDownload,
    setMaintainTime,
    setIsMobile,
    toggleSideFold,
    setSideFold,
    setLoginType,
    setAffilkaTag,
    setWalletType,
    setIsFiat,
    setHideZeroBalance,
    setWeekRewardRate,
    setMonthRewardRate,
    setWeekRewardApprovedRate,
    setMonthRewardApprovedRate,
    setSearchGameModalOpen,
    setGTMInitialized,
    setDevice,
} = counterSlice.actions;

export default counterSlice.reducer;
